import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ChevronDown, Download, Eye, Pencil, Phone, Plus, Trash2, X } from 'lucide-react';
import { useSelector } from 'react-redux';

const API = process.env.REACT_APP_API_URL;

const todayStr = () => new Date().toISOString().split('T')[0];

const inputCls =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white';

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ── Caller Multi-Select ───────────────────────────────────────────────────────
function CallerMultiSelect({ options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = id => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

  const allSelected = options.length > 0 && value.length === options.length;
  const label = value.length === 0 || allSelected
    ? 'All Callers'
    : value.length === 1
      ? options.find(o => o.id === value[0])?.name || '1 selected'
      : `${value.length} selected`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[130px] gap-2"
      >
        <span className={value.length === 0 || allSelected ? 'text-gray-400' : 'text-gray-700'}>{label}</span>
        <ChevronDown size={14} className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-20 mt-1 min-w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden max-h-48 overflow-y-auto">
          {options.length === 0
            ? <p className="px-3 py-2 text-sm text-gray-400">No callers</p>
            : <>
              <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                <input
                  type="checkbox"
                  checked={value.length === options.length}
                  ref={el => { if (el) el.indeterminate = value.length > 0 && value.length < options.length; }}
                  onChange={() => onChange(value.length === options.length ? [] : options.map(o => o.id))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">Select All</span>
              </label>
              {options.map(o => (
                <label key={o.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value.includes(o.id)}
                    onChange={() => toggle(o.id)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{o.name}</span>
                </label>
              ))}
            </>
          }
        </div>
      )}
    </div>
  );
}

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, color }) {
  const colors = {
    blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',  text: 'text-blue-700',  num: 'text-blue-600'  },
    orange: { bg: 'bg-orange-50', border: 'border-orange-200',text: 'text-orange-700',num: 'text-orange-500' },
    gray:   { bg: 'bg-gray-50',   border: 'border-gray-200',  text: 'text-gray-600',  num: 'text-gray-500'  },
    red:    { bg: 'bg-red-50',    border: 'border-red-200',   text: 'text-red-700',   num: 'text-red-500'   },
    yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200',text: 'text-yellow-700',num: 'text-yellow-600' },
    green:  { bg: 'bg-green-50',  border: 'border-green-200', text: 'text-green-700', num: 'text-green-600'  },
  };
  const c = colors[color] || colors.gray;
  return (
    <div className={`${c.bg} border ${c.border} rounded-xl p-3 flex flex-col gap-1`}>
      <span className={`text-xs font-semibold uppercase tracking-wide ${c.text}`}>{label}</span>
      <span className={`text-2xl font-bold ${c.num}`}>{value}</span>
    </div>
  );
}

// ── Shared form body used by Add & Edit modals ─────────────────────────────────
function ReportFormBody({ reportDate, setReportDate, totalCalls, setTotalCalls, stats, fetching, totalErr, setTotalErr, isEdit }) {
  const totalNum      = parseInt(totalCalls, 10) || 0;
  const sumKnown      = stats.warmLead + stats.callBack + stats.coldLead;
  const notInterested = Math.max(0, totalNum - sumKnown);

  return (
    <>
      {/* Date */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Report Date</label>
        <input
          type="date"
          className={inputCls}
          value={reportDate}
          max={todayStr()}
          onChange={e => setReportDate(e.target.value)}
        />
      </div>

      {/* Total Calls */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Total Calls *</label>
        <input
          type="number"
          min="0"
          className={`${inputCls} ${totalErr ? 'border-red-400' : ''}`}
          placeholder="Enter total calls made"
          value={totalCalls}
          onChange={e => { setTotalCalls(e.target.value); setTotalErr(''); }}
        />
        {totalErr && <p className="text-xs text-red-600 mt-1">{totalErr}</p>}
      </div>

      {/* Call Breakdown */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700">Call Breakdown</span>
          {fetching && (
            <span className="text-xs text-blue-500 flex items-center gap-1">
              <span className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin inline-block" />
              Fetching…
            </span>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          <StatCard label="Interested"      value={stats.warmLead}  color="orange" />
          <StatCard label="Call Back"      value={stats.callBack}  color="blue"   />
          <StatCard label="Follow Up"      value={stats.coldLead}  color="gray"   />
          <StatCard label="Not Interested" value={notInterested}   color="red"    />
          <StatCard label="Not Connected"  value={0}               color="yellow" />
          <StatCard label="Total Calls"    value={totalNum}        color="green"  />
        </div>

        {totalNum > 0 && totalNum < sumKnown && (
          <div className="mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            <p className="text-xs text-red-700">
              Total calls ({totalNum}) is less than Follow Up + Call Back + Follow Up ({sumKnown}) from the system. Please verify.
            </p>
          </div>
        )}
      </div>
    </>
  );
}

// ── Add Report Modal ───────────────────────────────────────────────────────────
function AddReportModal({ onClose, onSuccess, authConfig, userInfo }) {
  const [reportDate, setReportDate] = useState(todayStr());
  const [callers, setCallers]       = useState([]);
  const [rows, setRows]             = useState([{ id: 1, callerId: '', callerName: '', totalCalls: '', totalErr: '', stats: { warmLead: 0, callBack: 0, coldLead: 0 }, fetching: false }]);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr]   = useState('');

  useEffect(() => {
    axios.get(`${API}/users`, authConfig)
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];
        const myId = userInfo?.id || userInfo?._id;
        const assigned = all.filter(u => u.assignedTo?._id === myId || u.assignedTo === myId);
        const callerList = assigned.length > 0 ? assigned : (() => {
          const self = all.find(u => u._id === myId);
          return self ? [self] : [];
        })();

        setCallers(callerList);

        if (callerList.length > 0) {
          const date = todayStr();
          const initialRows = callerList.map(c => ({
            id: c._id,
            callerId: c._id,
            callerName: c.name,
            totalCalls: '',
            totalErr: '',
            stats: { warmLead: 0, callBack: 0, coldLead: 0 },
            fetching: true,
          }));
          setRows(initialRows);
          initialRows.forEach(async row => {
            try {
              const { data: sd } = await axios.get(`${API}/daily-call-report/stats?date=${date}&callerId=${row.callerId}`, authConfig);
              setRows(prev => prev.map(r => r.id === row.id ? { ...r, fetching: false, stats: { warmLead: sd.warmLead || 0, callBack: sd.callBack || 0, coldLead: sd.coldLead || 0 } } : r));
            } catch {
              setRows(prev => prev.map(r => r.id === row.id ? { ...r, fetching: false } : r));
            }
          });
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const fetchRowStats = async (rowId, callerId, date) => {
    if (!callerId) return;
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, fetching: true } : r));
    try {
      const { data } = await axios.get(`${API}/daily-call-report/stats?date=${date}&callerId=${callerId}`, authConfig);
      setRows(prev => prev.map(r => r.id === rowId ? { ...r, fetching: false, stats: { warmLead: data.warmLead || 0, callBack: data.callBack || 0, coldLead: data.coldLead || 0 } } : r));
    } catch {
      setRows(prev => prev.map(r => r.id === rowId ? { ...r, fetching: false } : r));
    }
  };

  // Re-fetch stats for all filled rows when date changes
  useEffect(() => {
    rows.forEach(r => { if (r.callerId) fetchRowStats(r.id, r.callerId, reportDate); });
  }, [reportDate]); // eslint-disable-line

  const handleCallerChange = (rowId, callerId) => {
    const found = callers.find(c => c._id === callerId);
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, callerId, callerName: found?.name || '', stats: { warmLead: 0, callBack: 0, coldLead: 0 }, totalErr: '' } : r));
    if (callerId) fetchRowStats(rowId, callerId, reportDate);
  };

  const handleTotalChange = (rowId, val) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, totalCalls: val, totalErr: '' } : r));
  };

  const addRow = () => {
    setRows(prev => [...prev, { id: Date.now(), callerId: '', callerName: '', totalCalls: '', totalErr: '', stats: { warmLead: 0, callBack: 0, coldLead: 0 }, fetching: false }]);
  };

  const removeRow = id => {
    if (rows.length === 1) return;
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    let hasErr = false;
    setRows(prev => prev.map(r => {
      const n = parseInt(r.totalCalls, 10);
      if (r.totalCalls === '' || isNaN(n) || n < 0) { hasErr = true; return { ...r, totalErr: 'Required' }; }
      return r;
    }));
    if (hasErr) return;
    setSubmitErr(''); setSubmitting(true);
    try {
      await Promise.all(rows.map(r => {
        const totalNum = parseInt(r.totalCalls, 10) || 0;
        const sumKnown = r.stats.warmLead + r.stats.callBack + r.stats.coldLead;
        const notInterested = Math.max(0, totalNum - sumKnown);
        return axios.post(`${API}/daily-call-report`, {
          reportDate, totalCalls: totalNum,
          warmLead: r.stats.warmLead, callBack: r.stats.callBack, coldLead: r.stats.coldLead,
          notInterested, notConnected: 0,
          ...(r.callerId && { callerId: r.callerId, callerName: r.callerName }),
        }, authConfig);
      }));
      onSuccess();
    } catch (err) {
      setSubmitErr(err?.response?.data?.message || 'Failed to submit report.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-blue-600" />
            <h2 className="text-base font-semibold text-gray-800">Add Daily Call Report</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          {/* Report Date */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Report Date</label>
            <input type="date" className={inputCls} value={reportDate} max={todayStr()} onChange={e => setReportDate(e.target.value)} />
          </div>

          {/* Table header */}
          <div className="grid gap-2" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Caller</span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Calls</span>
            <span />
          </div>

          {/* Rows */}
          {rows.map(row => (
            <div key={row.id} className="grid gap-2 items-start" style={{ gridTemplateColumns: '1fr 1fr auto' }}>
              <div className="flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700 font-medium min-h-[38px]">
                {row.callerName || '—'}
              </div>
              <div>
                <input
                  type="number" min="0"
                  className={`${inputCls} ${row.totalErr ? 'border-red-400' : ''}`}
                  placeholder="0"
                  value={row.totalCalls}
                  onChange={e => handleTotalChange(row.id, e.target.value)}
                />
                {row.totalErr && <p className="text-xs text-red-600 mt-0.5">{row.totalErr}</p>}
              </div>
              <button type="button" onClick={() => removeRow(row.id)}
                className="mt-1.5 text-gray-300 hover:text-red-400 transition-colors disabled:opacity-30"
                disabled={rows.length === 1}>
                <X size={15} />
              </button>
            </div>
          ))}

          {/* Add row */}
          <button type="button" onClick={addRow}
            className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium">
            <Plus size={14} /> Add
          </button>

          {submitErr && <p className="text-xs text-red-600">{submitErr}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
              {submitting ? 'Submitting…' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Edit Report Modal ──────────────────────────────────────────────────────────
function EditReportModal({ report, onClose, onSuccess, authConfig }) {
  const initDate = report.reportDate ? new Date(report.reportDate).toISOString().split('T')[0] : todayStr();
  const [reportDate, setReportDate] = useState(initDate);
  const [totalCalls, setTotalCalls] = useState(String(report.totalCalls ?? ''));
  const [stats, setStats]           = useState({
    warmLead: report.warmLead || 0,
    callBack: report.callBack || 0,
    coldLead: report.coldLead || 0,
  });
  const [fetching, setFetching]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [totalErr, setTotalErr]     = useState('');
  const [submitErr, setSubmitErr]   = useState('');

  const fetchStats = useCallback(async (date) => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/daily-call-report/stats?date=${date}`, authConfig);
      setStats({ warmLead: data.warmLead || 0, callBack: data.callBack || 0, coldLead: data.coldLead || 0 });
    } catch { /* ignore */ } finally { setFetching(false); }
  }, [authConfig]);

  useEffect(() => { fetchStats(reportDate); }, [reportDate, fetchStats]);

  const totalNum      = parseInt(totalCalls, 10) || 0;
  const sumKnown      = stats.warmLead + stats.callBack + stats.coldLead;
  const notInterested = Math.max(0, totalNum - sumKnown);

  const handleSubmit = async e => {
    e.preventDefault();
    if (!totalCalls || totalNum < 0) { setTotalErr('Total calls must be 0 or more.'); return; }
    setTotalErr(''); setSubmitErr(''); setSubmitting(true);
    try {
      await axios.put(`${API}/daily-call-report/${report._id}`, {
        reportDate, totalCalls: totalNum,
        warmLead: stats.warmLead, callBack: stats.callBack, coldLead: stats.coldLead,
        notInterested, notConnected: 0,
      }, authConfig);
      onSuccess();
    } catch (err) {
      setSubmitErr(err?.response?.data?.message || 'Failed to update report.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Pencil size={16} className="text-blue-600" />
            <h2 className="text-base font-semibold text-gray-800">Edit Daily Call Report</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-5">
          <ReportFormBody
            reportDate={reportDate} setReportDate={setReportDate}
            totalCalls={totalCalls} setTotalCalls={setTotalCalls}
            stats={stats} fetching={fetching}
            totalErr={totalErr} setTotalErr={setTotalErr}
            isEdit
          />
          {submitErr && <p className="text-xs text-red-600">{submitErr}</p>}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={submitting || fetching}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors">
              {submitting ? 'Updating…' : 'Update Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── View Report Modal ──────────────────────────────────────────────────────────
function ViewReportModal({ report, onClose }) {
  const rows = [
    { label: 'Caller',         value: report.callerName || '—' },
    { label: 'Report Date',    value: formatDate(report.reportDate) },
    { label: 'Submitted At',   value: formatDate(report.createdAt) },
    { label: 'Total Calls',    value: report.totalCalls ?? 0,    color: 'text-green-600' },
    { label: 'Interested (Warm)',  value: report.warmLead ?? 0,      color: 'text-orange-600' },
    { label: 'Call Back',         value: report.callBack ?? 0,      color: 'text-blue-600' },
    { label: 'Follow Up (Cold)',  value: report.coldLead ?? 0,      color: 'text-gray-600' },
    { label: 'Not Interested', value: report.notInterested ?? 0, color: 'text-red-600' },
    { label: 'Not Connected',  value: report.notConnected ?? 0,  color: 'text-yellow-600' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Eye size={16} className="text-blue-600" />
            <h2 className="text-base font-semibold text-gray-800">Report Details</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors"><X size={20} /></button>
        </div>

        <div className="px-5 py-4">
          <div className="divide-y divide-gray-100 rounded-xl border border-gray-100 overflow-hidden shadow-sm">
            {rows.map(({ label, value, color }) => (
              <div key={label} className="flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                <span className="text-sm text-gray-500 font-medium">{label}</span>
                <span className={`text-sm font-semibold ${color || 'text-gray-800'}`}>{value}</span>
              </div>
            ))}
          </div>

          <button
            onClick={onClose}
            className="mt-4 w-full border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DailyCallForm() {
  const { userInfo } = useSelector(s => s.userLogin || {});
  const { getRoleData } = useSelector(s => s.getRoleInfo || {});
  const role = getRoleData || userInfo?.role || {};
  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const [reports, setReports]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editReport, setEditReport]   = useState(null);
  const [viewReport, setViewReport]   = useState(null);
  const [toast, setToast]             = useState(null);
  const [filterFY, setFilterFY]         = useState('');
  const [filterCaller, setFilterCaller] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo]     = useState('');
  const [assignedCallers, setAssignedCallers] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  // Financial year helpers — only years that have report data
  const fyOptions = [...new Set(reports.map(r => {
    const d = new Date(r.reportDate);
    const m = d.getMonth() + 1;
    return m >= 4 ? d.getFullYear() : d.getFullYear() - 1;
  }))].sort((a, b) => b - a).map(y => ({
    value: String(y),
    label: `${y}-${String(y + 1).slice(-2)}`,
  }));
  const showToast = (type, msg) => setToast({ type, msg });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/daily-call-report`, authConfig);
      setReports(data.reports || []);
    } catch {
      showToast('error', 'Could not load reports.');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { fetchReports(); }, [fetchReports]);

  useEffect(() => {
    const myId = (userInfo?.id || userInfo?._id)?.toString();
    const isSuperAdmin = userInfo?.role?.name === 'Super Admin';
    axios.get(`${API}/users`, authConfig)
      .then(({ data }) => {
        const all = Array.isArray(data) ? data : [];

        if (isSuperAdmin) {
          setAssignedCallers(all.map(u => ({ id: u._id?.toString(), name: u.name })));
          return;
        }

        // BFS from the current user to collect all subordinates at any depth
        const childrenOf = {};
        all.forEach(u => {
          const parentId = (u.assignedTo?._id || u.assignedTo)?.toString();
          if (parentId) {
            if (!childrenOf[parentId]) childrenOf[parentId] = [];
            childrenOf[parentId].push({ id: u._id?.toString(), name: u.name });
          }
        });

        const result = [];
        const visited = new Set();
        const queue = [myId];
        while (queue.length > 0) {
          const cur = queue.shift();
          for (const child of (childrenOf[cur] || [])) {
            if (!visited.has(child.id)) {
              visited.add(child.id);
              result.push(child);
              queue.push(child.id);
            }
          }
        }

        if (result.length > 0) {
          setAssignedCallers(result);
        } else {
          // Regular caller: show only self
          const self = all.find(u => u._id?.toString() === myId);
          setAssignedCallers(self ? [{ id: self._id?.toString(), name: self.name }] : []);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleAddSuccess = () => {
    setShowAddModal(false);
    showToast('success', 'Report submitted successfully.');
    fetchReports();
  };

  const handleEditSuccess = () => {
    setEditReport(null);
    showToast('success', 'Report updated successfully.');
    fetchReports();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/daily-call-report/${deleteTarget._id}`, authConfig);
      setDeleteTarget(null);
      showToast('success', 'Report deleted successfully.');
      fetchReports();
    } catch (err) {
      showToast('error', err?.response?.data?.message || 'Failed to delete report.');
    } finally {
      setDeleting(false);
    }
  };

  const filteredReports = reports.filter(r => {
    const d = new Date(r.reportDate);
    const month = d.getMonth() + 1; // 1-12
    const year  = d.getFullYear();
    if (filterFY) {
      const fyStart = parseInt(filterFY);
      // FY: Apr fyStart → Mar fyStart+1
      const inFY = (year === fyStart && month >= 4) || (year === fyStart + 1 && month <= 3);
      if (!inFY) return false;
    }
    if (filterCaller.length > 0 && filterCaller.length < assignedCallers.length) {
      const id = r.callerId || r.caller?._id || r.caller || '';
      if (!filterCaller.includes(id)) return false;
    }
    if (filterDateFrom) {
      const from = new Date(filterDateFrom);
      from.setHours(0, 0, 0, 0);
      if (d < from) return false;
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo);
      to.setHours(23, 59, 59, 999);
      if (d > to) return false;
    }
    return true;
  });

  const hasFilters = filterFY || filterDateFrom || filterDateTo
    || (filterCaller.length > 0 && filterCaller.length < assignedCallers.length);

  const exportToCSV = () => {
    const headers = ['#', 'Caller', 'Report Date', 'Total Calls', 'Interested', 'Call Back', 'Follow Up', 'Not Interested', 'Not Connected', 'Submitted At'];
    const rows = filteredReports.map((r, i) => [
      i + 1,
      r.callerName || '',
      formatDate(r.reportDate),
      r.totalCalls ?? 0,
      r.warmLead ?? 0,
      r.callBack ?? 0,
      r.coldLead ?? 0,
      r.notInterested ?? 0,
      r.notConnected ?? 0,
      formatDate(r.createdAt),
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daily-call-reports-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[70] px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>
          {toast.msg}
        </div>
      )}

      {showAddModal && (
        <AddReportModal onClose={() => setShowAddModal(false)} onSuccess={handleAddSuccess} authConfig={authConfig} userInfo={userInfo} />
      )}
      {editReport && (
        <EditReportModal report={editReport} onClose={() => setEditReport(null)} onSuccess={handleEditSuccess} authConfig={authConfig} />
      )}
      {viewReport && (
        <ViewReportModal report={viewReport} onClose={() => setViewReport(null)} />
      )}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h2 className="text-base font-semibold text-gray-800 mb-2">Delete Report</h2>
            <p className="text-sm text-gray-500 mb-5">
              Are you sure you want to delete the report for <span className="font-medium text-gray-700">{deleteTarget.callerName || 'this caller'}</span> on <span className="font-medium text-gray-700">{formatDate(deleteTarget.reportDate)}</span>? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 border border-gray-300 text-gray-700 text-sm font-medium px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-5">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Daily Call Reports</h1>
            <p className="text-sm text-gray-500 mt-0.5">Track your daily call activity</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportToCSV}
              disabled={filteredReports.length === 0}
              className="inline-flex items-center gap-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-medium px-4 py-2.5 rounded-xl transition-colors shadow-sm"
            >
              <Download size={15} />
              Export
            </button>
            {!!role?.dailyCallReportAdd && (
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors shadow-sm"
              >
                <Plus size={16} />
                Add Report
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-4">
          <div className="flex flex-wrap gap-5 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Financial Year</label>
              <select
                value={filterFY}
                onChange={e => setFilterFY(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[130px]"
              >
                <option value="">All Years</option>
                {fyOptions.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">From Date</label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={e => setFilterDateFrom(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[130px]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">To Date</label>
              <input
                type="date"
                value={filterDateTo}
                onChange={e => setFilterDateTo(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[130px]"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Caller</label>
              <CallerMultiSelect options={assignedCallers} value={filterCaller} onChange={setFilterCaller} />
            </div>
            {hasFilters && (
              <button
                onClick={() => { setFilterFY(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterCaller([]); }}
                className="text-xs text-gray-500 hover:text-red-500 border border-gray-200 rounded-lg px-3 py-2 transition-colors"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Loading reports…</span>
            </div>
          ) : reports.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Phone size={36} className="mb-3 opacity-40" />
              <p className="text-sm font-medium">No reports yet</p>
              <p className="text-xs mt-1">Click "Add Report" to submit your first daily report</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">#</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Caller</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Report Date</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-green-600 uppercase tracking-wide">Total</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-orange-500 uppercase tracking-wide">Interested (W)</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-blue-500 uppercase tracking-wide">Call Back</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Follow Up (C)</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-red-500 uppercase tracking-wide">Not Interested</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-yellow-500 uppercase tracking-wide">Not Connected</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Submitted At</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredReports.map((r, i) => (
                    <tr key={r._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{r.callerName || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{formatDate(r.reportDate)}</td>
                      <td className="px-4 py-3 text-center font-semibold text-green-600">{r.totalCalls ?? 0}</td>
                      <td className="px-4 py-3 text-center text-orange-600 font-medium">{r.warmLead ?? 0}</td>
                      <td className="px-4 py-3 text-center text-blue-600 font-medium">{r.callBack ?? 0}</td>
                      <td className="px-4 py-3 text-center text-gray-600 font-medium">{r.coldLead ?? 0}</td>
                      <td className="px-4 py-3 text-center text-red-600 font-medium">{r.notInterested ?? 0}</td>
                      <td className="px-4 py-3 text-center text-yellow-600 font-medium">{r.notConnected ?? 0}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(r.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setViewReport(r)}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title="View"
                          >
                            <Eye size={15} />
                          </button>
                          {!!role?.dailyCallReportEdit && (
                            <button
                              onClick={() => setEditReport(r)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                              title="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                          )}
                          {!!role?.dailyCallReportDelete && (
                            <button
                              onClick={() => setDeleteTarget(r)}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
